import type { Brand } from '@seek/brand-types';
import { useGetFeatureToggleVariant } from '@seek/hirer-feature-toggle';
import type { JobDetailsView } from '@seek/job-details-view';
import { useTranslations } from '@vocab/react';
import { Box, Loader } from 'braid-design-system';
import type { ComponentProps } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector, shallowEqual } from 'react-redux';

import { JobPreview } from 'src/app/shared/components/JobPreview'; // this will come from metropolis
import { FEATURE_TOGGLE_CART_EDIT_FLOW } from 'src/app/shared/config/features';
import {
  PREVIEW_JOB_DETAILS_PRESSED,
  PREVIEW_PRINT_JOB_DETAILS_PRESSED,
  PREVIEW_PRINT_SEARCH_RESULTS_PRESSED,
  PREVIEW_SEARCH_RESULT_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { useFormattedWorkArrangements } from 'src/app/shared/hooks/useFormattedWorkArrangements';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { useWorkTypeTaxonomy } from 'src/app/shared/taxonomies/useWorkTypeTaxonomy';
import { isANZCountryCode } from 'src/app/utils/country';
import { getBrandingForPreview } from 'src/app/utils/getBrandingForPreview';
import { getPreviewCurrencyLabel } from 'src/app/utils/getPreviewCurrencyLabel';
import { getProductFeatureForPreview } from 'src/app/utils/getProductFeatureForPreview';
import { log } from 'src/app/utils/logging';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import type { AppState, GuidedItem } from 'src/types/appState';
import type { VideoProductPosition } from 'src/types/videoProductPosition';

import translations from './.vocab';

export const getBrand = (newBrand: Brand | undefined, hasCoverImage: boolean) =>
  newBrand && {
    ...newBrand,
    assets: {
      ...newBrand.assets,
      cover: hasCoverImage ? (newBrand.assets.cover ?? undefined) : undefined,
    },
  };

export const Preview = () => {
  const {
    aboutBusiness,
    advertiserName,
    benefits,
    brand,
    categoryText,
    displayPay,
    editFlowCheckout,
    flowType,
    isConfidential,
    isGuided,
    jobDescription,
    jobLocationCountry,
    jobLocationDisplayDescription,
    jobLocationText,
    jobSummary,
    jobTitle,
    keySellingPoints,
    logo,
    phoneNumber,
    productFeaturesFromRedux,
    qualifications,
    selectedRRQuestions,
    responsibilities,
    salaryCurrency,
    salaryText,
    subCategoryText,
    videoUrl,
    workType,
    workArrangement,
  } = useSelector(
    (state: AppState) => ({
      aboutBusiness: state.formData.aboutBusiness,
      advertiserName: state.session.advertiserName ?? '',
      benefits: state.formData.benefits,
      brand: state.formData.brand,
      categoryText: state.session.categoryText,
      displayPay: state.formData.displayPay,
      editFlowCheckout: state.session.editFlowCheckout,
      flowType: state.formData.flowType,
      isConfidential: state.formData.isConfidential,
      isGuided: state.formData.isGuided,
      jobDescription: state.formData.jobDescription,
      jobLocationCountry: state.session.jobLocationCountry,
      jobLocationDisplayDescription:
        state.session.jobLocationDisplayDescription,
      jobLocationText: state.session.jobLocationText,
      jobSummary: state.formData.jobSummary,
      jobTitle: state.formData.jobTitle,
      keySellingPoints: [
        state.formData.keySellingPoint1,
        state.formData.keySellingPoint2,
        state.formData.keySellingPoint3,
      ],
      logo: state.formData.brand?.assets.logo,
      phoneNumber: state.formData.phoneNumber,
      productFeaturesFromRedux: state.session.productFeatures,
      qualifications: state.formData.qualifications,
      selectedRRQuestions: state.session.selectedRRQuestions,
      responsibilities: state.formData.responsibilities,
      salaryCurrency: state.formData.salaryCurrency,
      salaryText: state.formData.salaryText,
      subCategoryText: state.session.subCategoryText,
      videoUrl: state.formData.videoUrl,
      workType: state.formData.workType,
      workArrangement: state.formData.workArrangement,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);

  const { cartFeatures } = useShoppingCart();

  const { getFormattedWorkArrangements } = useFormattedWorkArrangements();

  const isCartEditFlowFeatureEnabled = useGetFeatureToggleVariant(
    FEATURE_TOGGLE_CART_EDIT_FLOW,
  )?.value;

  const handleTabClick = (index: number) => {
    const eventData = {
      eventName: Boolean(index)
        ? PREVIEW_SEARCH_RESULT_PRESSED
        : PREVIEW_JOB_DETAILS_PRESSED,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };

  const handlePrintClick = (index: number) => {
    const eventData = {
      eventName: Boolean(index)
        ? PREVIEW_PRINT_SEARCH_RESULTS_PRESSED
        : PREVIEW_PRINT_JOB_DETAILS_PRESSED,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };

  const getAdvertiserName = () =>
    isConfidential ? t('preview-private-advertiser') : advertiserName;

  const { getWorkTypeText } = useWorkTypeTaxonomy();

  const createGuidedList = (
    id: string,
    title: string,
    guidedItems?: GuidedItem[],
  ): string => {
    let guidedBlock = '';
    if (guidedItems && guidedItems?.length > 0) {
      guidedBlock += `<p id=${id}><strong>${title}</strong></p>`;
      guidedBlock += `<ul aria-labelledby=${id}>`;
      guidedItems?.map((q) => {
        guidedBlock += `<li>${q.value}</li>`;
      });
      guidedBlock += '</ul>';
    }

    return guidedBlock;
  };

  const createJobDescription = (): string => {
    let guidedJobDescription = '';

    if (aboutBusiness) {
      guidedJobDescription += `<p><strong>${t(
        'preview-guided-about-title',
      )}</strong></p>`;
      guidedJobDescription += `<p>${aboutBusiness}</p>`;
    }

    guidedJobDescription += createGuidedList(
      'tasks-responsibilities',
      t('preview-guided-responsibilities-title'),
      responsibilities,
    );
    guidedJobDescription += createGuidedList(
      'qualifications-experience',
      t('preview-guided-qualifications-title'),
      qualifications,
    );
    guidedJobDescription += createGuidedList(
      'benefits',
      t('preview-guided-benefits-title'),
      benefits,
    );

    return guidedJobDescription;
  };

  // TODO - use getProductFeatureForPreview directly once the CartEditFlow is 100% rolled out
  const { hasCoverImage, isBrandingEnabled } = getBrandingForPreview({
    cartFeatures,
    editFlowCheckout,
    flowType,
    isCartEditFlowFeatureEnabled: Boolean(isCartEditFlowFeatureEnabled),
    productFeaturesFromRedux,
  });

  const isKeySellingPointsEnabled = Boolean(
    getProductFeatureForPreview({
      featureType: 'BulletPoints',
      productFeaturesFromCart: cartFeatures,
      productFeaturesFromPostedJob: productFeaturesFromRedux,
    }),
  );

  const shouldRenderUrgentTag = Boolean(
    getProductFeatureForPreview({
      featureType: 'DisplayTag',
      productFeaturesFromCart: cartFeatures,
      productFeaturesFromPostedJob: productFeaturesFromRedux,
    }),
  );

  const shouldRenderFeaturedTag = Boolean(
    getProductFeatureForPreview({
      featureType: 'SponsoredSlot',
      productFeaturesFromCart: cartFeatures,
      productFeaturesFromPostedJob: productFeaturesFromRedux,
    }),
  );

  const salaryCurrencyText = getPreviewCurrencyLabel(
    salaryCurrency,
    jobLocationCountry,
  );

  const job: ComponentProps<typeof JobDetailsView>['job'] = {
    applyUrl: 'about:blank',
    branding:
      !isConfidential && isBrandingEnabled
        ? getBrand(brand, hasCoverImage)
        : undefined,
    classification: categoryText,
    company: {
      name: getAdvertiserName(),
    },
    contactMatches: [],
    currencyLabel: salaryCurrencyText,
    description: isGuided ? createJobDescription() : jobDescription,
    employerQuestions: selectedRRQuestions,
    location: jobLocationDisplayDescription,
    phoneNumber,
    postedDateFormatted: t('preview-posted-date'),
    salary: displayPay ? salaryText : `[${t('preview-hidden-salary-text')}]`,
    subClassification: subCategoryText,
    title: jobTitle,
    url: 'about:blank',
    video: videoUrl
      ? {
          link: videoUrl,
          position: 'BOTTOM' as VideoProductPosition,
        }
      : undefined,
    workType: getWorkTypeText(workType) ?? '',
    isVerified: true,
    restrictedApplication: {},
  };

  const showBullets = Boolean(isCartEditFlowFeatureEnabled)
    ? isKeySellingPointsEnabled && keySellingPoints.length > 0
    : (isKeySellingPointsEnabled || editFlowCheckout.enabled) &&
      keySellingPoints.length > 0;

  const formattedWorkArrangement = workArrangement
    ? getFormattedWorkArrangements([workArrangement])
    : '';

  const serp = {
    advertiserName: getAdvertiserName(),
    bullets: keySellingPoints,
    category: categoryText,
    displayPay,
    isFeatured: shouldRenderFeaturedTag,
    jobTitle,
    location: jobLocationText,
    ...(isBrandingEnabled && { logo }),
    salaryText,
    salaryCurrency: salaryCurrencyText,
    searchDescription: jobSummary,
    showBullets,
    subcategory: subCategoryText,
    tags: shouldRenderUrgentTag
      ? [
          {
            type: 'URGENT',
            label: isANZCountryCode(jobLocationCountry)
              ? t('immediate-start-label')
              : t('urgent-tag-label'),
          },
        ]
      : [],
    workArrangement: formattedWorkArrangement,
  };

  return (
    <ErrorBoundary
      onError={(error, info) => {
        log.error('Preview section | Failed to render', { error, info });
      }}
      fallback={
        <Box
          width="full"
          alignItems="center"
          justifyContent="center"
          style={{ display: 'flex' }}
        >
          <Loader />
        </Box>
      }
    >
      <JobPreview
        jobDetails={job}
        serpDetails={serp}
        onTabClick={handleTabClick}
        onPrintClick={handlePrintClick}
      />
    </ErrorBoundary>
  );
};
